import { useState, useCallback } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { useNutritionPresets, useDeleteNutritionPreset } from '../../hooks/useNutritionPresets'
import { NutritionPreset } from '../../types'
import { PresetForm } from './PresetForm'
import { Button } from '../ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import { Plus, Edit, Copy, Trash2 } from 'lucide-react'

export function NutritionPresetsTab() {
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [editingPreset, setEditingPreset] = useState<NutritionPreset | null>(null)
  const { user } = useAuth()
  const { data: presets, isLoading } = useNutritionPresets(user?.id || '')
  const deleteMutation = useDeleteNutritionPreset()

  const handleDelete = useCallback(async (id: string, name: string) => {
    if (window.confirm(`Delete preset "${name}"? This won't affect existing plan items.`)) {
      try {
        await deleteMutation.mutateAsync(id)
      } catch (error) {
        console.error('Failed to delete preset:', error)
        alert('Failed to delete preset. Please try again.')
      }
    }
  }, [deleteMutation])

  const handleDuplicate = useCallback((preset: NutritionPreset) => {
    const { id, created_at, updated_at, ...presetData } = preset
    setEditingPreset({
      ...presetData,
      id: '',
      name: `${preset.name} (Copy)`,
      created_at: null,
      updated_at: null
    } as NutritionPreset)
  }, [])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading presets...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold">Nutrition Presets</h2>
          <p className="text-gray-600">Save nutrition items for quick reuse in your race plans</p>
        </div>
        <Button onClick={() => setShowAddDialog(true)} aria-label="Add new nutrition preset">
          <Plus className="h-4 w-4 mr-2" aria-hidden="true" />
          Add Preset
        </Button>
      </div>

      {!presets || presets.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <Plus className="h-12 w-12 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No presets yet</h3>
          <p className="text-gray-600 mb-4">Create your first nutrition preset to speed up race planning</p>
          <Button onClick={() => setShowAddDialog(true)} aria-label="Create your first nutrition preset">
            <Plus className="h-4 w-4 mr-2" aria-hidden="true" />
            Create First Preset
          </Button>
        </div>
      ) : (
        <Table aria-label="Nutrition presets">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Carbs (g)</TableHead>
              <TableHead>Fluids (ml)</TableHead>
              <TableHead>Sodium (mg)</TableHead>
              <TableHead>Caffeine (mg)</TableHead>
              <TableHead className="w-[120px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {presets.map((preset) => (
              <TableRow key={preset.id}>
                <TableCell className="font-medium">{preset.name}</TableCell>
                <TableCell>{preset.carbs_g || '-'}</TableCell>
                <TableCell>{preset.fluids_ml || '-'}</TableCell>
                <TableCell>{preset.sodium_mg || '-'}</TableCell>
                <TableCell>{preset.caffeine_mg || '-'}</TableCell>
                <TableCell>
                  <div className="flex space-x-1">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setEditingPreset(preset)}
                      aria-label={`Edit ${preset.name}`}
                    >
                      <Edit className="h-3 w-3" aria-hidden="true" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleDuplicate(preset)}
                      aria-label={`Duplicate ${preset.name}`}
                    >
                      <Copy className="h-3 w-3" aria-hidden="true" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleDelete(preset.id, preset.name)}
                      aria-label={`Delete ${preset.name}`}
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 className="h-3 w-3" aria-hidden="true" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <PresetForm
        open={showAddDialog || !!editingPreset}
        onOpenChange={(open: boolean) => {
          if (!open) {
            setShowAddDialog(false)
            setEditingPreset(null)
          }
        }}
        preset={editingPreset}
      />
    </div>
  )
}